import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Theme, useMediaQuery } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { Booking, Investment } from "@rentablo/api/apiv1";
import { useInfiniteListAndSearchAllBookings } from "@api/v1/hooks/useBookings";
import { useRangeSelectControl, useSecurityImages, useTableSort } from "@hooks";
import { TableSummary } from "@components";
import { AutoTable, LoadingPlaceHolder } from "@rentablo/components";
import { useAssetClassFilter } from "../assetClassFilter";
import { useBookingsTableConfig } from "./useBookingsTableConfig";
import {
  BookingInvestmentModal,
  BookingListView,
  BookingMobileList,
  investmentLogo,
} from "./mobile";

const PER_PAGE = 50;

interface BookingsTableProps {
  summaryValue: number;
  accountIds: number[];
  rangeProps: ReturnType<typeof useRangeSelectControl>;
  displayCurrency?: string;
  summaryCurrency?: string;
  // Shown in the phone list's header line while it has entries, e.g. Export.
  mobileHeaderAction?: ReactNode;
  // Shown at the end of that line even when it is empty, so a filter that
  // leaves no entries can still be changed.
  mobileSettingsAction?: ReactNode;
  // The page's investments, so a tapped booking opens its overlay right away.
  knownInvestments?: Investment[];
  // Sort of the phone list, chosen in its settings panel.
  mobileView?: BookingListView;
}

export const BookingsTableContainer = ({
  summaryValue,
  rangeProps,
  accountIds = [],
  displayCurrency,
  summaryCurrency,
  mobileHeaderAction,
  mobileSettingsAction,
  knownInvestments,
  mobileView,
}: BookingsTableProps) => {
  const { range } = rangeProps;
  const [selectedBooking, setSelectedBooking] = useState<Booking>();
  const { investmentTypes } = useAssetClassFilter();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { t } = useTranslation("");

  const { sort, sortField, sortDirection } = useTableSort({
    field: "valueDate",
    direction: "DESC",
  });

  // Phones sort in their settings panel; by date the list is grouped by month.
  const order = isSm
    ? [mobileView?.order ?? "valueDate desc"]
    : sortField && sortDirection
      ? [`${sortField} ${sortDirection.toLowerCase()}`]
      : undefined;

  const {
    data,
    isPending,
    isFetching,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteListAndSearchAllBookings(
    {
      accountId: accountIds,
      // The list loads page by page, so the server applies the filter.
      investmentType: investmentTypes.length ? investmentTypes : undefined,
      // The type filter lives in the phone settings panel only.
      type: isSm && mobileView?.types.length ? mobileView.types : undefined,
      minDate: range?.startDate?.toISOString(),
      maxDate: range?.endDate.toISOString(),
      order,
      perPage: PER_PAGE,
      displayCurrency,
    },
    {
      enabled: accountIds.length > 0,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        const page = lastPage.paging?.page ?? 0;
        const pageCount = lastPage.paging?.pageCount ?? 0;
        return page < pageCount ? page + 1 : undefined;
      },
    }
  );

  const bookings: Booking[] = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.bookings ?? []),
    [data]
  );

  const config = useBookingsTableConfig({
    sortField,
    sortDirection,
    onSort: sort,
  });

  const logoSecurity = useMemo(
    () =>
      bookings.map((booking) => ({
        hasValidIsin: true,
        type:
          booking.investment.quoteProvider === "currency"
            ? "81_fcurr"
            : "11_stock",
        isin: booking.investment.isin || booking.investment.tickerSymbol,
        ticker_symbol: booking.investment.tickerSymbol,
        quoteProvider: booking.investment.quoteProvider,
      })),
    [bookings]
  );

  const logos = useSecurityImages(logoSecurity);

  if (isPending && accountIds.length > 0) {
    return <LoadingPlaceHolder />;
  }

  if (isSm) {
    return (
      <div>
        <BookingMobileList
          bookings={bookings}
          logos={logos}
          totalCount={
            data?.pages[0]?.paging?.totalCount ??
            (hasNextPage ? undefined : bookings.length)
          }
          groupByMonth={mobileView?.groupByMonth ?? true}
          headerAction={mobileHeaderAction}
          settingsAction={mobileSettingsAction}
          isRefreshing={isPlaceholderData}
          hasMore={Boolean(hasNextPage)}
          isLoadingMore={isFetchingNextPage}
          loadMoreFailed={isFetchNextPageError}
          onLoadMore={() => {
            // Each page once, and none for a list that is about to be replaced.
            if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
              fetchNextPage();
            }
          }}
          showEndOfList={(data?.pages.length ?? 0) > 1}
          onRowClick={setSelectedBooking}
        />
        <BookingInvestmentModal
          investmentId={selectedBooking?.investment.id}
          knownInvestments={knownInvestments}
          image={
            selectedBooking && investmentLogo(logos, selectedBooking.investment)
          }
          onClose={() => setSelectedBooking(undefined)}
        />
      </div>
    );
  }

  const loader = isFetchingNextPage ? <LoadingPlaceHolder /> : null;

  return (
    <div>
      <TableSummary
        summaryValue={summaryValue}
        currencySymbol={summaryCurrency ?? displayCurrency}
        rangeProps={rangeProps}
        hideMobileRangeSelector
      />

      <AutoTable
        settings={config}
        data={bookings}
        additionalProps={{ logos }}
        isFetching={isFetching && !isFetchingNextPage}
        paging={{
          type: "infinite",
          hasMore: Boolean(hasNextPage),
          next: () => {
            fetchNextPage();
          },
          loader,
        }}
        showNoData={bookings.length === 0}
        noDataFirstLine={t("components.depotsStatistic.bookingsTable.noItems")}
      />
    </div>
  );
};
